import React from 'react';

import { NextPageWithLayout } from 'next';

import {
  META_FEATURES_CANONICAL,
  META_FEATURES_DESCRIPTION,
  META_FEATURES_TITLE,
} from '../../constants/metadata';
import { AppDownloadSection } from '../../components/AppDownloadSection/AppDownloadSection';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { HeaderSection } from './components/HeaderSection';
import { MILLION_CATCHES, MILLION_USERS } from '../../constants/constants';
import { CircleImageAndText } from './components/CircleImageAndText';
import fishingSpotsPhone from './FishingSpotsPage/assets/phoneOne.png';
import fishingForecastPhone from './FishingForecastsPage/assets/phoneOne.png';
import localAnglerPhone from './FishingSpotsPage/assets/phoneTwo.png';
import waypointsPhone from './WaypointsPage/assets/phoneOne.png';
import depthMapPhone from './DepthMapsPage/assets/phoneOne.png';
import logbookPhone from './LogbookPage/assets/phoneFour.png';

import depthMapBackground from './DepthMapsPage/assets/depthMapBackground.png';

import { FeatureDetailsSection } from './components/FeatureDetailsSection';
import { DownloadAppHorizontalCTA } from '../../components/CTAs/DownloadAppHorizontalCTA/DownloadAppHorizontalCTA';
import { Container } from '../../components/Container/Container';

export const FeaturesPage: NextPageWithLayout = () => {
  return (
    <div>
      <MetaTags
        title={META_FEATURES_TITLE}
        description={META_FEATURES_DESCRIPTION}
        link={META_FEATURES_CANONICAL}
      />
      <HeaderSection
        headingText="Find fishing spots with Fishbrain"
        bodyText={`With over ${MILLION_CATCHES} million verified catches on Fishbrain, you'll be surprised to find fishing spots you didn't even know about. 
        Download to find spots in your local area based on the species you want to catch.`}
      />
      <CircleImageAndText
        imgSrc={fishingSpotsPhone}
        imgAlt="Instant overviews of each fishing water in the Fishbrain app"
        headingText={`${MILLION_CATCHES} million catch locations`}
        readMoreLink="/features/fishing-spots"
        bodyText="Explore catch locations, and filter by species, in any body of water you like to fish. Narrow your scope to catches made in a specific month or season to hone in on the perfect strategy. Planning your fishing trip with Fishbrain gives you all the information you need about local boat ramps, marinas, bait shops, license vendors, and more."
      />

      <FeatureDetailsSection
        headingText="Depth maps"
        bodyText="Depth maps allow you to create a strategy on where to find fish based on observable structures and the depth of waters. Since certain species are more likely to be found at certain depths, using depth maps allow you to create a strategy on where to find fish. "
        readMoreLink="/features/depth-maps"
        imgSrc={depthMapPhone}
        imgAlt="Find new fishing spots in the Fishbrain app"
        bgImage={depthMapBackground}
        featuresList={[
          {
            id: 1,
            headingText: 'Contour lines',
            bodyText: [
              'Contour lines and color indicate how deep or shallow bodies of waters are and can also assist in finding structures underwater. ',
            ],
          },
          {
            id: 2,
            headingText: 'Underwater structures',
            bodyText: [
              'Lighter colors surrounded by dark waters might for instance indicate reefs and since many fish tend to hide among them it lets you as an angler know the best spot to fish.',
            ],
          },
          {
            id: 3,
            headingText: 'Fishing strategy',
            bodyText: [
              'When creating your fishing strategy take note of depth changes, look for structures you might find promising as well as anything else of interest to find your prime fishing spot. ',
            ],
          },
        ]}
      />
      <Container className="mt-sm">
        <DownloadAppHorizontalCTA className="mx-16 mt-16" />
      </Container>

      <CircleImageAndText
        imgSrc={waypointsPhone}
        imgAlt="Instant overviews of each fishing water in the Fishbrain app"
        headingText="Plan and prepare with Waypoints"
        readMoreLink="/features/waypoints"
        appOnlyPill
        bodyText="In case something catches your eye when strategizing or out on the water, you can make note of it in the app with the help of waypoints! You’ll never again forget the exact spot of a honey hole, the reef bustling with fish or the log you wanted to but didn’t have the time to investigate."
      />
      <FeatureDetailsSection
        headingText="Learn from local anglers"
        bodyText={`Connect with other anglers and find new fishing buddies on Fishbrain. Our community of over ${MILLION_USERS} million anglers means you can always find other anglers to share tips and tricks with- and show off your latest PB to.`}
        imgSrc={localAnglerPhone}
        imgAlt="Learn from local anglers"
        featuresList={[
          {
            id: 1,
            headingText: 'Share',
            bodyText: [
              'Share pics of your latest catch and have your fellow anglers there to cheer you on. See what people are catching near you.',
            ],
          },
          {
            id: 2,
            headingText: 'Connect',
            bodyText: [
              `Use our extensive network of over ${MILLION_USERS} million anglers to find new fishing buddies and plan a local trip.`,
            ],
          },
          {
            id: 3,
            headingText: 'Learn',
            bodyText: [
              'Our active community boasts tons of great groups around topics like bass fishing to advice for beginners.',
            ],
          },
        ]}
      />
      <CircleImageAndText
        imgSrc={fishingForecastPhone}
        imgAlt="fishing forcast"
        headingText="Fishing forecast"
        readMoreLink="/features/fishing-forecasts"
        appOnlyPill
        bodyText={`Know the best time to hit the water for any species. Using data from over ${MILLION_CATCHES} million verified catches, and comparing weather conditions, we allow you to access the fishing forecast for a specific body of water. With Fishbrain Pro you can also see weather forecasts and patterns 7 days in advance, allowing you to pick the best day and best time to fish.`}
      />
      <CircleImageAndText
        imgSrc={logbookPhone}
        imgAlt="fishing forcast"
        headingText="Logbook"
        readMoreLink="/features/logbook"
        appOnlyPill
        imgRight
        bodyText="When you’ve caught your fish, make sure to log your catches in Fishbrain’s Logbook app to keep track of what, where and when you caught a fish as well as what you caught it with. Logging your catches is essential for any angler who wants to improve as it can help you spot patterns in your fishing."
      />
      <AppDownloadSection />
    </div>
  );
};
